import React, { useState } from "react";
import { Box, useTheme } from "@mui/material";
import Button from "../../components/Button";
import AddIcon from "@mui/icons-material/Add";
import { pushErrorMessage } from '../../utils/notifications';
import StopReasonCodeGroupsDataGrid from "./components/stopReasonCodeGroupsDataGrid";
import Popup from '../../components/Popup';
import { FormattedMessage } from "react-intl";
import messages from "./components/messages";
import AddEditStopReasonCodeGroup from "./components/stopReasonCodeGroupAddEditDialog";
import { tokens } from "../../assets/theme";
import { useQuery } from "@tanstack/react-query";
import { getStopReasonCodeGroups, updateStopReasonCodeGroup, createStopReasonCodeGroup, deleteStopReasonCodeGroup } from "./hooks/fetchers";
import CircularProgress from "@mui/material/CircularProgress";
import { useMsal, useAccount } from "@azure/msal-react";
import AxionControls from "../../components/controls/AxionControls";
import { useLocalStorage } from "../../hooks/useLocalStorage";

const StopReasonCodeGroups = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { accounts } = useMsal();
    const account = useAccount(accounts[0]);
    const [companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openPopup, setOpenPopup] = useState(false);
    const [stopReasonCodeGroup, setStopReasonCodeGroup] = useState({
        id: "",
        name: "",
        description: "",
        createdBy: "",
        sortOrder: 0,
        companyKey: companyKey
    });
    const [showBackdrop, setShowBackdrop] = useState(false);

    const stopReasonCodeGroupsQuery = useQuery(["stopReasonCodeGroups", companyKey], () => getStopReasonCodeGroups(companyKey),
        {
            staleTime: 120000,
            refetchInterval: 120000,
        }
    );

    const handleAddStopReasonCodeGroupClick = () => {
        setStopReasonCodeGroup({
            id: "",
            name: "",
            description: "",
            createdBy: "",
            sortOrder: 0,
            companyKey: companyKey
        });
        setOpenPopup(true);
    }

    const handleEditStopReasonCodeGroupClick = (id) => {
        const stopReasonGroup = stopReasonCodeGroupsQuery.data?.find((rowData) => rowData.id === id);
        if (!stopReasonGroup) {
            return;
        }
        const editableGroup = {
            ...stopReasonGroup,
            description: stopReasonGroup.description ?? ""
        };
        setStopReasonCodeGroup(editableGroup);
        setOpenPopup(true);
    }

    const handleDeleteStopReasonCodeGroupClick = (id) => {
        handleDelete(id);
    }

    const handleChange = (event) => {
        const { id, name, value, type, checked } = event.target
        setStopReasonCodeGroup(prev => {
            return {
                ...prev,
                [id ? id : name]: type === "checkbox" ? checked : value
            }
        })
    }

    const handleSave = () => {
        setShowBackdrop(true);
        if (stopReasonCodeGroup.id) {
            stopReasonCodeGroup.updatedBy = account.username;
            updateStopReasonCodeGroup(stopReasonCodeGroup).then(() => {
                setOpenPopup(false);
                stopReasonCodeGroupsQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot update the stop reason code group.`);
            });
        } else {
            stopReasonCodeGroup.updatedBy = account.username;
            stopReasonCodeGroup.createdBy = account.username;
            createStopReasonCodeGroup(stopReasonCodeGroup).then(() => {
                setOpenPopup(false);
                stopReasonCodeGroupsQuery.refetch();
                setShowBackdrop(false);
            }).catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot create the stop reason code group.`);
            });
        }
    }

    const handleDelete = (id) => {
        setShowBackdrop(true);
        deleteStopReasonCodeGroup(id).then(() => {
            stopReasonCodeGroupsQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
                console.log(error);
                setShowBackdrop(false);
                pushErrorMessage(`Cannot delete the stop reason code group.`);
            });
    }


    if (stopReasonCodeGroupsQuery.isLoading)
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress color="secondary" /></div>;

    return (
        <Box maxWidth={1800} >
            <Button
                text={<FormattedMessage {...messages.addStopReasonCodeGroupLabel} />}
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddStopReasonCodeGroupClick}
                sx={{
                    "&:hover": {
                        backgroundColor: colors.dashbordGreen[700],
                    },
                    backgroundColor: colors.dashbordGreen[500],
                    color: "#000000",
                    position: "absolute",
                    right: "10px",
                }} />
            <StopReasonCodeGroupsDataGrid
                stopReasonCodeGroups={stopReasonCodeGroupsQuery}
                handleDeleteStopReasonCodeGroupClick={handleDeleteStopReasonCodeGroupClick}
                handleEditStopReasonCodeGroupClick={handleEditStopReasonCodeGroupClick} />
            <Popup
                openPopup={openPopup}
                setOpenPopup={setOpenPopup}
                maxWidht="sm"
                title={stopReasonCodeGroup.id !== "" ? <FormattedMessage {...messages.editStopReasonCodeGroupLabel} /> : <FormattedMessage {...messages.addStopReasonCodeGroupLabel} />}>
                <AddEditStopReasonCodeGroup
                    onChange={handleChange}
                    handleSave={handleSave}
                    stopReasonCodeGroup={stopReasonCodeGroup}
                    setOpenPopup={setOpenPopup} />
            </Popup>
            <AxionControls.AxionBackdrop open={showBackdrop} />
        </Box>
    );
};

export default StopReasonCodeGroups;
